import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { useRepoList } from '../lib/repos';
import { type DispatchOutcome, dispatchTask, type NewTaskInput } from '../lib/task-dispatch';
import { Modal } from './Modal';
import { NewTaskForm } from './NewTaskForm';
import { NewTaskResult } from './NewTaskResult';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// Dispatch a task from the console (#1034). Container — resolves the repo list
// the picker offers, owns the POST, and hands the verdict to <NewTaskResult/>.
// Two files, two reasons to change: what a human types vs. what the dispatcher
// decided.
//
// The repo list is the SHARED `useRepoList` (`lib/repos.ts`), never a second
// fetch: `/v1/repos` advertises no filter, so the exhaust walk that makes the
// Repos grid's search honest is the only correct read of "the account's enabled
// repos" — and its cache is warm from any other surface that asked.
//
// LOADING AND FAILED ARE NOT EMPTY. The picker's whole vocabulary is that walk,
// and the form's "no enabled repos yet" line is a claim ABOUT THE ACCOUNT — so a
// walk still in flight, or one that failed outright, must not render it: both
// would send a maintainer off to enable a repo that already is. This used to be
// `loadedRepos(repos) ?? []`, which is that claim in one token — `loadedRepos`
// answers `undefined` for in-flight AND failed precisely so a caller cannot make
// it. The three states go through <QueryBoundary/> over the paged view, the same
// shape <StartPlanDialog/> holds over the same read. A walk that lands EMPTY
// still reads as empty; that is the one case the sentence is true.
//
// `onDispatched` fires only when a queue row may exist. An escalation and a
// dupe mint none, so re-reading the board for them would ask the server to
// confirm nothing happened; an `unclear` 2xx names no task but may well have
// minted one, and the server is the only place that answer lives.

export type NewTaskModalProps = {
  readonly open: boolean;
  readonly token: string | null;
  readonly onClose: () => void;
  /** A task exists now — re-read the queue from the server (never patch an echo). */
  readonly onDispatched: () => void;
  /** Cross-link: open the detail drawer on the dispatched / surviving task. */
  readonly onOpenTask: (taskId: string) => void;
};

export function NewTaskModal(props: NewTaskModalProps): JSX.Element {
  const repos = useRepoList(props.token);
  const [pending, setPending] = createSignal(false);
  const [outcome, setOutcome] = createSignal<DispatchOutcome | null>(null);
  // The repo the answered ask named — a duped PR link can't be built without it.
  const [asked, setAsked] = createSignal('');

  const submit = async (input: NewTaskInput): Promise<void> => {
    setPending(true);
    const result = await dispatchTask(input, props.token);
    setAsked(input.repo_full_name);
    setOutcome(result);
    setPending(false);
    if (result.kind === 'dispatched' || result.kind === 'queued' || result.kind === 'unclear') {
      props.onDispatched();
    }
  };

  const close = (): void => {
    setOutcome(null);
    props.onClose();
  };

  return (
    <Modal
      open={props.open}
      size="lg"
      title={t('fleet.new_task.title')}
      desc={t('fleet.new_task.intro')}
      onClose={close}
    >
      <Show
        when={outcome()}
        fallback={
          <QueryBoundary query={repos.view} loading={<Skeleton height="12rem" />}>
            {(rows) => (
              <NewTaskForm
                repos={rows}
                pending={pending()}
                onSubmit={(input) => void submit(input)}
                onCancel={close}
              />
            )}
          </QueryBoundary>
        }
      >
        {(result) => (
          <NewTaskResult
            outcome={result()}
            repoFullName={asked()}
            onOpenTask={(id) => {
              close();
              props.onOpenTask(id);
            }}
            onAgain={() => setOutcome(null)}
            onClose={close}
          />
        )}
      </Show>
    </Modal>
  );
}
